// Module frontend surface.
//
// El shell descubre este fichero solo (modules/*/frontend/index.js) y mezcla lo
// que exporta. Añadir este módulo no toca la plataforma (§4.2).
//
// Dos rutas para un módulo entero, y no es poco código escondido: un gestor
// documental es UNA pantalla por la que se navega. Partirlo en «carpetas»,
// «archivos» y «buscar» sería tres sitios donde mirar para responder siempre a
// la misma pregunta. La papelera sí es su propia ruta porque es el único sitio
// donde las acciones significan lo contrario que en el archivador.

// Con la extensión: la guarda de i18n del shell importa este fichero con Node.
import assistantMessages from './assistantMessages.js'

export const routes = [
  {
    path: '/documents',
    name: 'documents',
    component: () => import('./pages/DrivePage.vue'),
    meta: { permission: 'documents:read', title: 'documents.title' , ownsQuery: true /* la carpeta abierta va en la consulta: el armazon no la remonta al cambiar */ },
  },
  {
    path: '/documents/trash',
    name: 'documents-trash',
    component: () => import('./pages/TrashPage.vue'),
    meta: { permission: 'documents:delete', title: 'documents.trash.title' },
  },
  // La otra mitad del §13: lo que alguien te comparte dentro de una carpeta que
  // no ves no aparece en la raíz —enseñarla sería decir que existe—, así que
  // tiene que haber una puerta. Esta.
  //
  // Detrás de `documents:read` y no de un permiso propio: es una vista de lo
  // mismo, y quien abre el archivador la puede abrir. Que esté vacía para casi
  // todo el mundo es lo normal y es correcto.
  {
    path: '/documents/shared',
    name: 'documents-shared',
    component: () => import('./pages/SharedPage.vue'),
    meta: { permission: 'documents:read', title: 'documents.shared.title' },
  },
]

/**
 * Una sola entrada, sin hijos.
 *
 * La papelera NO es una entrada de menú, y es deliberado: se llega a ella desde
 * el archivador, que es donde estás cuando te das cuenta de que has borrado
 * algo. Un módulo que gasta dos de las pocas ranuras de la barra lateral —una
 * de ellas para un sitio al que se va tres veces al año— le quita una al
 * siguiente módulo que se instale.
 *
 * Detrás de `documents:read`, que es el permiso que abre la pantalla a la que
 * apunta: el shell dibuja la fila a quien pueda abrir su destino, así que
 * guardarla con otro permiso daría un enlace que responde 403 al pulsarlo.
 */
export const navigation = [
  {
    to: '/documents',
    labelKey: 'documents.title',
    permission: 'documents:read',
    icon: 'folder',
    group: 'business',
  },
]

// Los textos que aporta este módulo, mezclados rama a rama por idioma. Las
// claves del shell ganan en caso de choque, así que un módulo no puede
// reescribir la copia de la plataforma.
//
// Los cuatro idiomas, y no es cortesía: un módulo cuyas traducciones no viajan
// con él no se puede vender al tercer cliente. Incluye cómo se llama este
// módulo en las pantallas de la PLATAFORMA —Roles, Ajustes, Auditoría y el
// panel del asistente— porque un módulo que obliga a añadir claves al shell no
// es extraíble (§4.2).
export const messages = {
  es: {
    errors: {
      DOCUMENT_TOO_LARGE: 'Ese documento es demasiado grande para leerlo en una conversación.',
    },
    documents: {
      title: 'Documentos',
      subtitle: 'El archivador de la empresa: carpetas, ficheros y quién los tocó',
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Documentos",
        subtitle: "Quién entra en cada carpeta, qué se mira antes de guardar un fichero y cómo sale uno de la empresa",
      },
      root: 'Documentos',
      breadcrumb: 'Dónde estás',
      // Lo que se dibuja en lugar de las carpetas de arriba cuando alguien
      // llega a algo que le han compartido dentro de un sitio que no ve (§13).
      breadcrumbCut: 'Hay carpetas por encima a las que no tienes acceso',
      searchPlaceholder: 'Buscar en documentos…',
      searchingIn: 'Resultados de «{term}»',
      dashboard: {
        storage: 'Almacenamiento',
        storageHelp: 'Qué porcentaje de la cuota de documentos está ocupado, papelera incluida.',
        recent: 'Últimos documentos',
        recentHelp: 'Los cinco ficheros subidos más recientemente, con la carpeta donde están.',
        documents: 'Documentos',
        documentsHelp: 'Cuántos ficheros hay archivados, sin contar los de la papelera.',
      },
      types: {
        folder: 'Carpeta',
        pdf: 'PDF',
        image: 'Imagen',
        document: 'Documento',
        spreadsheet: 'Hoja de cálculo',
        presentation: 'Presentación',
        archive: 'Comprimido',
        text: 'Texto',
        other: 'Otro',
      },
      references: {
        customer: 'Cliente',
        project: 'Obra',
        work_order: 'Orden de trabajo',
        asset: 'Máquina',
        supplier: 'Proveedor',
        vehicle: 'Vehículo',
        maintenance_plan: 'Plan de mantenimiento',
        invoice: 'Factura',
        material: 'Material',
        property: 'Emplazamiento',
        work: 'Obra de construcción',
      },
      actions: {
        new: 'Nuevo',
        newFolder: 'Nueva carpeta',
        upload: 'Subir archivo',
        create: 'Crear',
        cancel: 'Cancelar',
        open: 'Abrir',
        download: 'Descargar',
        rename: 'Renombrar',
        move: 'Mover a…',
        moveHere: 'Mover aquí',
        details: 'Detalles',
        info: 'Información',
        delete: 'Eliminar',
        restore: 'Restaurar',
        purge: 'Eliminar definitivamente',
        link: 'Vincular',
        unlink: 'Desvincular',
        clearSearch: 'Quitar la búsqueda',
        share: 'Compartir',
      },
      confirm: {
        unlink: '«{name}» dejará de aparecer en esta ficha. El archivo NO se borra: sigue en el archivador, en su carpeta.',
      },
      table: {
        name: 'Nombre',
        type: 'Tipo',
        size: 'Tamaño',
        owner: 'Subido por',
        created: 'Creado',
        updated: 'Modificado',
      },
      filters: {
        title: 'Filtros',
        clear: 'Quitar los filtros',
        type: 'Tipo',
        allTypes: 'Todos',
        when: 'Fecha',
        anyTime: 'Cualquier fecha',
        today: 'Hoy',
        week: 'Últimos 7 días',
        month: 'Últimos 30 días',
        custom: 'Entre dos fechas',
        from: 'Desde',
        to: 'Hasta',
        sort: 'Ordenar por',
      },
      sort: {
        nameAsc: 'Nombre (A-Z)',
        nameDesc: 'Nombre (Z-A)',
        newest: 'Más recientes',
        oldest: 'Más antiguos',
        changed: 'Modificados hace poco',
        largest: 'Más grandes',
        smallest: 'Más pequeños',
      },
      storage: {
        title: 'Almacenamiento',
        used: '{used} de {total}',
      },
      empty: {
        folder: 'Esta carpeta está vacía',
        folderHint: 'Sube archivos o crea una carpeta para empezar.',
        noResults: 'No se han encontrado archivos',
        noResultsHint: 'Prueba con otra palabra, o con parte del nombre.',
        filtered: 'Sin resultados',
        filteredHint: 'Prueba a quitar algún filtro.',
      },
      folder: {
        newTitle: 'Nueva carpeta',
        name: 'Nombre de la carpeta',
        placeholder: 'Clientes, Proyectos, Documentación interna…',
        required: 'Escribe un nombre.',
        info: '{name} · en {path} · creada el {created}',
      },
      rename: {
        title: 'Renombrar',
        label: 'Nombre nuevo',
      },
      move: {
        title: 'Mover «{name}»',
        currently: 'Ahora está en {path}',
        filterPlaceholder: 'Filtrar carpetas…',
        noMatch: 'Ninguna carpeta coincide',
        done: 'Movido',
      },
      delete: {
        fileTitle: '¿Eliminar este archivo?',
        fileBody: '«{name}» pasa a la papelera. Podrás restaurarlo desde allí.',
        folderTitle: '¿Eliminar esta carpeta?',
        folderBody: '«{name}» pasa a la papelera con todo lo que hay dentro. Podrás restaurarlo desde allí.',
        done: 'Enviado a la papelera',
      },
      details: {
        title: 'Detalles',
        type: 'Tipo',
        size: 'Tamaño',
        location: 'Ubicación',
        uploadedBy: 'Subido por',
        uploadedAt: 'Fecha de subida',
        updatedAt: 'Última modificación',
        originalName: 'Nombre original',
        unknownUser: 'Sin asignar',
        access: 'Quién tiene acceso',
        onlyYou: 'Solo tú. Nadie más lo ve.',
        andMore: 'y {count} más',
        linkedTo: 'Vinculado a',
        notLinked: 'No está vinculado a ninguna ficha.',
        linkTo: 'Vincular a una ficha',
        pickType: 'Elige qué tipo de ficha',
        linked: 'Vinculado',
        checks: 'Lo que se encontró dentro',
      },
      // Lo que la inspección de la plataforma encontró dentro de un fichero y
      // dejó pasar. Espejo de platform/core/backend/uploads/.
      flags: {
        pdf_javascript: 'Este PDF lleva JavaScript dentro. No puede salir del visor ni conectarse a ningún sitio, pero fuera del ERP sí puede hacer más.',
        pdf_auto_action: 'Este PDF hace algo por su cuenta al abrirse: puede sacar un aviso en pantalla.',
        pdf_dynamic_form: 'Este PDF es un formulario dinámico (XFA). Puede verse en blanco fuera de Acrobat.',
        pdf_embedded_media: 'Este PDF lleva contenido multimedia incrustado.',
        pdf_embedded_file: 'Este PDF lleva otro archivo dentro. Es lo normal en una factura electrónica Factur-X.',
        archive_contents_unknown: 'Es un comprimido: nadie ha visto lo que hay dentro hasta que se abre.',
        not_scanned: 'No se pudo pasar el antivirus cuando se subió.',
        scanned: 'Analizado por el antivirus al subirlo.',
      },
      viewer: {
        zoomIn: 'Ampliar',
        zoomOut: 'Reducir',
        noPreview: 'Este tipo de archivo no se puede ver aquí dentro. Descárgalo para abrirlo con tu programa.',
      },
      upload: {
        queue: 'Subidas',
        dropHere: 'Suelta los archivos aquí',
        uploading: 'Subiendo {count} archivo | Subiendo {count} archivos',
        uploaded: '{count} archivo subido | {count} archivos subidos',
        failed: '{count} archivo no se ha subido | {count} archivos no se han subido',
        finished: '{count} archivo subido | {count} archivos subidos',
        finishing: 'Guardando…',
        done: 'Listo',
        cancel: 'Cancelar esta subida',
        cancelled: 'Cancelado',
        tooBig: 'Ocupa {size} y el máximo por archivo es {max}.',
      },
      // ── Compartir ──────────────────────────────────────────────────────
      share: {
        title: 'Compartir «{name}»',
        peopleHeading: 'Personas con acceso',
        addLabel: 'Dar acceso a alguien',
        addHelp: 'Escribe un nombre o un correo. Solo aparecen personas de esta empresa.',
        searchPlaceholder: 'Nombre o correo…',
        noPeople: 'Nadie con ese nombre',
        grant: 'Dar acceso',
        granted: '{name} ya tiene acceso',
        revoke: 'Quitar acceso',
        revoked: '{name} ya no tiene acceso',
        revokeTitle: '¿Quitarle el acceso a {name}?',
        revokeMessage: '{name} dejará de ver «{what}» y todo lo que hay dentro. El acceso se corta al momento, no dentro de un rato.',
        done: 'Listo',
        privateNote: 'Lo que no compartes no lo ve nadie más, aunque trabaje aquí. Estar en la empresa no da acceso: hay que darlo.',
        levels: {
          viewer: 'Lector',
          editor: 'Editor',
          manager: 'Gestor',
          owner: 'Propietario',
        },
        from: {
          owner: 'Propietario',
          direct: 'Acceso directo',
          inherited: 'Acceso heredado de: {folder}',
          inheritedShort: 'de {folder}',
        },
        external: {
          heading: 'Enlace para alguien de fuera',
          unavailable: 'Los enlaces para gente de fuera no están activados en esta instalación. Pídeselo a quien administra el ERP.',
          new: 'Crear un enlace',
          create: 'Crear el enlace',
          copy: 'Copiar',
          copied: 'Enlace copiado',
          copyManually: 'No se ha podido copiar solo. Selecciona el enlace y cópialo a mano.',
          copyNow: 'Cópialo ahora: no se puede volver a ver',
          copyNote: 'Este enlace no se guarda en ningún sitio. Si lo pierdes, crea otro y anula este.',
          passwordLabel: 'Contraseña (opcional)',
          passwordHelp: 'Si la pones, mándala por otro sitio: una contraseña en el mismo correo que el enlace no protege de nada.',
          expiresLabel: 'Caduca el',
          expiresHelp: 'Vacío usa los días por defecto de Ajustes.',
          never: 'Que no caduque nunca',
          neverHint: 'Un enlace sin caducidad solo se cierra anulándolo a mano.',
          withPassword: 'Con contraseña',
          until: 'Hasta el {date}',
          noExpiry: 'Sin caducidad',
          opened: 'Sin abrir | Abierto una vez | Abierto {count} veces',
          revoke: 'Anular',
          revoked: 'Enlace anulado',
          revokeTitle: '¿Anular este enlace?',
          revokeMessage: 'Quien tenga «{name}» dejará de ver nada al momento, incluso si lo tiene abierto ahora mismo. No se puede volver a activar: habría que crear otro.',
          status: {
            revoked: 'Anulado',
            expired: 'Caducado',
          },
        },
      },
      shared: {
        title: 'Compartido conmigo',
        subtitle: 'Lo que otras personas te han dado y no es tuyo',
        backToDrive: 'Ir al archivador',
        owner: 'Propietario',
        access: 'Acceso',
        since: 'Desde',
        emptyTitle: 'Nadie te ha compartido nada todavía',
        emptyHint: 'Cuando alguien te dé acceso a una carpeta o a un documento, aparecerá aquí.',
      },
      notifications: {
        shared: {
          label: 'Cuando alguien te comparte una carpeta o un documento',
          title: 'Te han compartido «{name}»',
          body: '{who} te ha dado acceso a «{name}» como {level}.',
        },
      },
      trash: {
        title: 'Papelera',
        subtitle: 'Lo que se ha eliminado, y todavía se puede recuperar',
        explain: 'Lo que hay aquí sigue ocupando espacio hasta que se elimina definitivamente. Vaciar la papelera es lo que libera almacenamiento.',
        deletedBy: 'Eliminado por',
        deletedAt: 'Fecha',
        emptyState: 'La papelera está vacía',
        emptyStateHint: 'Aquí aparece lo que elimines, hasta que lo borres del todo.',
        empty: 'Vaciar la papelera',
        emptyTitle: '¿Vaciar la papelera?',
        emptyBody: 'Se elimina definitivamente todo lo que hay en ella. Esto no se puede deshacer.',
        emptied: 'Se han eliminado {count} archivos',
        purgeTitle: '¿Eliminar definitivamente?',
        purgeBody: '«{name}» se borra del todo. Esto no se puede deshacer.',
        purged: 'Eliminado definitivamente',
        restoredInto: '«{name}» ha vuelto a {folder}',
        restoredRoot: '«{name}» ha vuelto a Documentos',
      },
      pager: {
        previous: 'Anterior',
        next: 'Siguiente',
        of: 'Página {page} de {pages}',
      },
    },
    modules: { documents: 'Documentos' },
    assistant: {
      openers: {
        documents: {
          find: '¿Dónde está el contrato de…?',
          space: '¿Cuánto espacio queda en Documentos?',
          recent: '¿Qué se ha subido últimamente?',
        },
      },
    },
    permissions: {
      documents: {
        read: 'Ver y descargar documentos',
        write: 'Subir, crear carpetas, renombrar y mover',
        delete: 'Enviar a la papelera y restaurar',
        manage: 'Vaciar la papelera y borrar definitivamente',
        // §53: administrar la instancia no es leer los papeles de la empresa.
        admin: 'Ver y administrar TODO el archivador, de todos',
        share_external: 'Compartir por enlace con quien no tiene cuenta aquí',
      },
    },
    settings: {
      ns: {
        documents: {
          title: 'Documentos',
          subtitle: 'Cuánto puede guardar, y qué se archiva solo',
          storageQuotaGb: {
            label: 'Almacenamiento para documentos (GB)',
            help: 'Cuánto puede ocupar el archivador entero. Lo que hay en la papelera cuenta también, porque sigue ocupando disco: vaciarla es lo que libera espacio.',
          },
          maxFileMb: {
            label: 'Tamaño máximo por archivo (MB)',
            help: 'Un contrato escaneado o una foto de móvil caben en 25 MB. Subirlo admite archivos más grandes, y hace que una conexión lenta tarde más en descubrir que uno se ha rechazado.',
          },
          externalLinkDays: {
            label: 'Días que dura un enlace externo',
            help: 'Lo que dura un enlace compartido con alguien sin cuenta en el ERP cuando quien lo crea no dice otra cosa. Puede ponerse otra fecha al crearlo, y un enlace sin caducidad necesita el permiso que borra definitivamente.',
          },
          folderForCustomer: {
            label: 'Carpeta para cada cliente',
            help: 'Vacío es apagado. Escribe la ruta de la carpeta que se abrirá al dar de alta un cliente, por ejemplo «Clientes / [customer]». Palabras disponibles: [customer], [code], [year], [month]. Varias carpetas de una vez, separadas por punto y coma: la primera es la del cliente.',
          },
          folderForProject: {
            label: 'Carpeta para cada obra',
            help: 'Vacío es apagado. Escríbela dentro del cliente para tener una carpeta por obra dentro de cada cliente: «[folder:customer] / [project]». Palabras: [project], [customer], [code], [year], [month], y [folder:customer] para la carpeta que ese cliente ya tiene.',
          },
          folderForWorkOrder: {
            label: 'Carpeta para cada orden de trabajo',
            help: 'Vacío es apagado. Palabras: [code], [customer], [year], [month], [folder:customer].',
          },
          folderForProductionOrder: {
            label: 'Carpeta para cada orden de fabricación',
            help: 'Vacío es apagado. Palabras: [order], [code], [year], [month].',
          },
          folderForSupplier: {
            label: 'Carpeta para cada proveedor',
            help: 'Vacío es apagado. Palabras: [supplier], [code], [year], [month].',
          },
          folderForProperty: {
            label: 'Carpeta para cada emplazamiento',
            help: 'Vacío es apagado. Palabras: [property], [customer], [code], [year], [month], [folder:customer].',
          },
          folderForAsset: {
            label: 'Carpeta para cada máquina',
            help: 'Vacío es apagado. Palabras: [asset], [code], [site], [year], [month].',
          },
          folderForSopPlan: {
            label: 'Carpeta para cada ciclo de planificación',
            help: 'Vacío es apagado. Por ejemplo «Planificación / [from] a [to]». Palabras: [plan], [code], [site], [from], [to], [year], [month]. [from] y [to] son los meses que cubre el plan.',
          },
          fileQuotes: {
            label: 'Archivar cada presupuesto enviado',
            help: 'Vacío es apagado: el presupuesto se imprime y se descarga, y no se guarda copia. Por ejemplo «[folder:customer] / Presupuestos». Palabras: [customer], [project], [code], [year], [month], [folder:customer], [folder:project].',
          },
          fileInvoices: {
            label: 'Archivar cada factura emitida',
            help: 'Vacío es apagado. Por ejemplo «[folder:customer] / Facturas / [year]». Palabras: [customer], [project], [number], [series], [year], [month], [folder:customer], [folder:project]. La copia que se guarda es la que se emitió, y no se sustituye nunca.',
          },
          filePurchaseOrders: {
            label: 'Archivar cada pedido de compra enviado',
            help: 'Vacío es apagado. Por ejemplo «[folder:supplier] / Pedidos». Palabras: [supplier], [code], [year], [month], [folder:supplier].',
          },
          fileProductionOrders: {
            label: 'Archivar cada orden de fabricación impresa',
            help: 'Vacío es apagado. Por ejemplo «[folder:project] / Fabricación». Palabras: [order], [code], [project], [year], [month], [folder:project].',
          },
          groups: {
            storage: 'Espacio',
            folders: 'Carpetas automáticas',
            papers: 'Papeles que genera el ERP',
          },
        },
      },
    },
    audit: {
      actions: {
        documents: {
          uploaded: 'Archivo subido',
          renamed: 'Archivo renombrado',
          moved: 'Archivo movido',
          deleted: 'Archivo enviado a la papelera',
          restored: 'Archivo restaurado',
          purged: 'Archivo eliminado definitivamente',
          downloaded: 'Archivo descargado',
          linked: 'Archivo vinculado a una ficha',
          unlinked: 'Archivo desvinculado de una ficha',
          folder_created: 'Carpeta creada',
          folder_adopted: 'Carpeta sin dueño asignada',
          folder_renamed: 'Carpeta renombrada',
          folder_moved: 'Carpeta movida',
          folder_deleted: 'Carpeta enviada a la papelera',
          folder_restored: 'Carpeta restaurada',
          folder_purged: 'Carpeta eliminada definitivamente',
          trash_emptied: 'Papelera vaciada',
          viewed: 'Archivo abierto en el visor',
          permission_granted: 'Acceso concedido',
          permission_changed: 'Acceso modificado',
          permission_revoked: 'Acceso retirado',
          external_link_shared: 'Enlace externo creado',
          external_link_revoked: 'Enlace externo anulado',
        },
      },
    },
  },

  ca: {
    errors: {
      DOCUMENT_TOO_LARGE: 'Aquest document és massa gran per llegir-lo en una conversa.',
    },
    documents: {
      title: 'Documents',
      subtitle: "L'arxivador de l'empresa: carpetes, fitxers i qui els ha tocat",
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Documents",
        subtitle: "Qui entra a cada carpeta, què es mira abans de desar un fitxer i com en surt un de l'empresa",
      },
      root: 'Documents',
      breadcrumb: 'On ets',
      breadcrumbCut: 'Hi ha carpetes per sobre a les quals no tens accés',
      searchPlaceholder: 'Cerca als documents…',
      searchingIn: 'Resultats de «{term}»',
      dashboard: {
        storage: 'Emmagatzematge',
        storageHelp: 'Quin percentatge de la quota de documents està ocupat, paperera inclosa.',
        recent: 'Últims documents',
        recentHelp: 'Els cinc fitxers pujats més recentment, amb la carpeta on són.',
        documents: 'Documents',
        documentsHelp: 'Quants fitxers hi ha arxivats, sense comptar els de la paperera.',
      },
      types: {
        folder: 'Carpeta',
        pdf: 'PDF',
        image: 'Imatge',
        document: 'Document',
        spreadsheet: 'Full de càlcul',
        presentation: 'Presentació',
        archive: 'Comprimit',
        text: 'Text',
        other: 'Altre',
      },
      references: {
        customer: 'Client',
        project: 'Obra',
        work_order: 'Ordre de treball',
        asset: 'Màquina',
        supplier: 'Proveïdor',
        vehicle: 'Vehicle',
        maintenance_plan: 'Pla de manteniment',
        invoice: 'Factura',
        material: 'Material',
        property: 'Emplaçament',
        work: 'Obra de construcció',
      },
      actions: {
        new: 'Nou',
        newFolder: 'Nova carpeta',
        upload: 'Puja un fitxer',
        create: 'Crea',
        cancel: 'Cancel·la',
        open: 'Obre',
        download: 'Baixa',
        rename: 'Reanomena',
        move: 'Mou a…',
        moveHere: 'Mou aquí',
        details: 'Detalls',
        info: 'Informació',
        delete: 'Elimina',
        restore: 'Restaura',
        purge: 'Elimina definitivament',
        link: 'Vincula',
        unlink: 'Desvincula',
        clearSearch: 'Treu la cerca',
        share: 'Compartir',
      },
      confirm: {
        unlink: '«{name}» deixarà d’aparèixer en aquesta fitxa. L’arxiu NO s’esborra: continua a l’arxivador, a la seva carpeta.',
      },
      table: {
        name: 'Nom',
        type: 'Tipus',
        size: 'Mida',
        owner: 'Pujat per',
        created: 'Creat',
        updated: 'Modificat',
      },
      filters: {
        title: 'Filtres',
        clear: 'Treure els filtres',
        type: 'Tipus',
        allTypes: 'Tots',
        when: 'Data',
        anyTime: 'Qualsevol data',
        today: 'Avui',
        week: 'Últims 7 dies',
        month: 'Últims 30 dies',
        custom: 'Entre dues dates',
        from: 'Des de',
        to: 'Fins a',
        sort: 'Ordena per',
      },
      sort: {
        nameAsc: 'Nom (A-Z)',
        nameDesc: 'Nom (Z-A)',
        newest: 'Més recents',
        oldest: 'Més antics',
        changed: 'Modificats fa poc',
        largest: 'Més grans',
        smallest: 'Més petits',
      },
      storage: {
        title: 'Emmagatzematge',
        used: '{used} de {total}',
      },
      empty: {
        folder: 'Aquesta carpeta és buida',
        folderHint: 'Puja fitxers o crea una carpeta per començar.',
        noResults: "No s'ha trobat cap fitxer",
        noResultsHint: 'Prova amb una altra paraula, o amb part del nom.',
        filtered: 'Sense resultats',
        filteredHint: 'Prova a treure algun filtre.',
      },
      folder: {
        newTitle: 'Nova carpeta',
        name: 'Nom de la carpeta',
        placeholder: 'Clients, Projectes, Documentació interna…',
        required: 'Escriu un nom.',
        info: '{name} · a {path} · creada el {created}',
      },
      rename: {
        title: 'Reanomena',
        label: 'Nom nou',
      },
      move: {
        title: 'Mou «{name}»',
        currently: 'Ara és a {path}',
        filterPlaceholder: 'Filtra carpetes…',
        noMatch: 'Cap carpeta coincideix',
        done: 'Mogut',
      },
      delete: {
        fileTitle: 'Vols eliminar aquest fitxer?',
        fileBody: '«{name}» passa a la paperera. El podràs restaurar des d’allà.',
        folderTitle: 'Vols eliminar aquesta carpeta?',
        folderBody: '«{name}» passa a la paperera amb tot el que hi ha dins. Ho podràs restaurar des d’allà.',
        done: 'Enviat a la paperera',
      },
      details: {
        title: 'Detalls',
        type: 'Tipus',
        size: 'Mida',
        location: 'Ubicació',
        uploadedBy: 'Pujat per',
        uploadedAt: 'Data de pujada',
        updatedAt: 'Última modificació',
        originalName: 'Nom original',
        unknownUser: 'Sense assignar',
        access: 'Qui hi té accés',
        onlyYou: 'Només tu. Ningú més ho veu.',
        andMore: 'i {count} més',
        linkedTo: 'Vinculat a',
        notLinked: 'No està vinculat a cap fitxa.',
        linkTo: 'Vincula a una fitxa',
        pickType: 'Tria quin tipus de fitxa',
        linked: 'Vinculat',
        checks: 'Què s’hi va trobar a dins',
      },
      // El que la inspecció de la plataforma va trobar dins d’un fitxer i va
      // deixar passar. Mirall de platform/core/backend/uploads/.
      flags: {
        pdf_javascript: 'Aquest PDF porta JavaScript a dins. No pot sortir del visor ni connectar-se enlloc, però fora de l’ERP sí que pot fer-ne més.',
        pdf_auto_action: 'Aquest PDF fa alguna cosa pel seu compte en obrir-se: pot mostrar un avís a la pantalla.',
        pdf_dynamic_form: 'Aquest PDF és un formulari dinàmic (XFA). Pot veure’s en blanc fora d’Acrobat.',
        pdf_embedded_media: 'Aquest PDF porta contingut multimèdia incrustat.',
        pdf_embedded_file: 'Aquest PDF porta un altre fitxer a dins. És el normal en una factura electrònica Factur-X.',
        archive_contents_unknown: 'És un comprimit: ningú no ha vist què hi ha a dins fins que s’obre.',
        not_scanned: 'No es va poder passar l’antivirus quan es va pujar.',
        scanned: 'Analitzat per l’antivirus en pujar-lo.',
      },
      viewer: {
        zoomIn: 'Amplia',
        zoomOut: 'Redueix',
        noPreview: "Aquest tipus de fitxer no es pot veure aquí dins. Baixa'l per obrir-lo amb el teu programa.",
      },
      upload: {
        queue: 'Pujades',
        dropHere: 'Deixa anar els fitxers aquí',
        uploading: 'Pujant {count} fitxer | Pujant {count} fitxers',
        uploaded: '{count} fitxer pujat | {count} fitxers pujats',
        failed: "{count} fitxer no s'ha pujat | {count} fitxers no s'han pujat",
        finished: '{count} fitxer pujat | {count} fitxers pujats',
        finishing: 'Desant…',
        done: 'Fet',
        cancel: 'Cancel·la aquesta pujada',
        cancelled: 'Cancel·lat',
        tooBig: 'Ocupa {size} i el màxim per fitxer és {max}.',
      },
      share: {
        title: 'Compartir «{name}»',
        peopleHeading: 'Persones amb accés',
        addLabel: 'Donar accés a algú',
        addHelp: 'Escriu un nom o un correu. Només hi surten persones d’aquesta empresa.',
        searchPlaceholder: 'Nom o correu…',
        noPeople: 'Ningú amb aquest nom',
        grant: 'Donar accés',
        granted: '{name} ja té accés',
        revoke: 'Treure accés',
        revoked: '{name} ja no té accés',
        revokeTitle: 'Treure l’accés a {name}?',
        revokeMessage: '{name} deixarà de veure «{what}» i tot el que hi ha a dins. L’accés es talla a l’instant, no d’aquí una estona.',
        done: 'Fet',
        privateNote: 'El que no comparteixes no ho veu ningú més, encara que hi treballi. Ser de l’empresa no dóna accés: s’ha de donar.',
        levels: {
          viewer: 'Lector',
          editor: 'Editor',
          manager: 'Gestor',
          owner: 'Propietari',
        },
        from: {
          owner: 'Propietari',
          direct: 'Accés directe',
          inherited: 'Accés heretat de: {folder}',
          inheritedShort: 'de {folder}',
        },
        external: {
          heading: 'Enllaç per a algú de fora',
          unavailable: "Els enllaços per a gent de fora no estan activats en aquesta instal·lació. Demana-ho a qui administra l'ERP.",
          new: 'Crear un enllaç',
          create: "Crear l'enllaç",
          copy: 'Copiar',
          copied: 'Enllaç copiat',
          copyManually: "No s'ha pogut copiar sol. Selecciona l'enllaç i copia'l a mà.",
          copyNow: 'Copia\u2019l ara: no es pot tornar a veure',
          copyNote: "Aquest enllaç no es desa enlloc. Si el perds, crea'n un altre i anul·la aquest.",
          passwordLabel: 'Contrasenya (opcional)',
          passwordHelp: "Si la poses, envia-la per un altre lloc: una contrasenya al mateix correu que l'enllaç no protegeix de res.",
          expiresLabel: 'Caduca el',
          expiresHelp: 'Buit fa servir els dies per defecte d\u2019Ajustos.',
          never: 'Que no caduqui mai',
          neverHint: 'Un enllaç sense caducitat només es tanca anul·lant-lo a mà.',
          withPassword: 'Amb contrasenya',
          until: 'Fins el {date}',
          noExpiry: 'Sense caducitat',
          opened: 'Sense obrir | Obert una vegada | Obert {count} vegades',
          revoke: 'Anul·lar',
          revoked: 'Enllaç anul·lat',
          revokeTitle: '¿Anul·lar aquest enllaç?',
          revokeMessage: "Qui tingui «{name}» deixarà de veure res a l'instant, fins i tot si el té obert ara mateix. No es pot tornar a activar: caldria crear-ne un altre.",
          status: {
            revoked: 'Anul·lat',
            expired: 'Caducat',
          },
        },
      },
      shared: {
        title: 'Compartit amb mi',
        subtitle: 'El que altres persones t’han donat i no és teu',
        backToDrive: 'Anar a l’arxivador',
        owner: 'Propietari',
        access: 'Accés',
        since: 'Des de',
        emptyTitle: 'Encara no t’han compartit res',
        emptyHint: 'Quan algú et doni accés a una carpeta o a un document, apareixerà aquí.',
      },
      notifications: {
        shared: {
          label: 'Quan algú et comparteix una carpeta o un document',
          title: 'T’han compartit «{name}»',
          body: '{who} t’ha donat accés a «{name}» com a {level}.',
        },
      },
      trash: {
        title: 'Paperera',
        subtitle: "El que s'ha eliminat, i encara es pot recuperar",
        explain: "El que hi ha aquí segueix ocupant espai fins que s'elimina definitivament. Buidar la paperera és el que allibera emmagatzematge.",
        deletedBy: 'Eliminat per',
        deletedAt: 'Data',
        emptyState: 'La paperera és buida',
        emptyStateHint: 'Aquí apareix el que eliminis, fins que ho esborris del tot.',
        empty: 'Buida la paperera',
        emptyTitle: 'Vols buidar la paperera?',
        emptyBody: "S'elimina definitivament tot el que hi ha. Això no es pot desfer.",
        emptied: "S'han eliminat {count} fitxers",
        purgeTitle: 'Vols eliminar-ho definitivament?',
        purgeBody: '«{name}» s’esborra del tot. Això no es pot desfer.',
        purged: 'Eliminat definitivament',
        restoredInto: '«{name}» ha tornat a {folder}',
        restoredRoot: '«{name}» ha tornat a Documents',
      },
      pager: {
        previous: 'Anterior',
        next: 'Següent',
        of: 'Pàgina {page} de {pages}',
      },
    },
    modules: { documents: 'Documents' },
    assistant: {
      openers: {
        documents: {
          find: 'On és el contracte de…?',
          space: 'Quant espai queda a Documents?',
          recent: "Què s'ha pujat últimament?",
        },
      },
    },
    permissions: {
      documents: {
        read: 'Veure i baixar documents',
        write: 'Pujar, crear carpetes, reanomenar i moure',
        delete: 'Enviar a la paperera i restaurar',
        manage: 'Buidar la paperera i esborrar definitivament',
        admin: 'Veure i administrar TOT l’arxivador, de tothom',
        share_external: 'Compartir per enllaç amb qui no té compte aquí',
      },
    },
    settings: {
      ns: {
        documents: {
          title: 'Documents',
          subtitle: 'Quant pot guardar, i què s’arxiva sol',
          storageQuotaGb: {
            label: 'Emmagatzematge per a documents (GB)',
            help: "Quant pot ocupar l'arxivador sencer. El que hi ha a la paperera també compta, perquè segueix ocupant disc: buidar-la és el que allibera espai.",
          },
          maxFileMb: {
            label: 'Mida màxima per fitxer (MB)',
            help: 'Un contracte escanejat o una foto de mòbil caben en 25 MB. Pujar-ho admet fitxers més grans, i fa que una connexió lenta trigui més a descobrir que un fitxer s’ha rebutjat.',
          },
          externalLinkDays: {
            label: 'Dies que dura un enllaç extern',
            help: "El que dura un enllaç compartit amb algú sense compte a l'ERP quan qui el crea no diu una altra cosa. Es pot posar una altra data en crear-lo, i un enllaç sense caducitat necessita el permís que esborra definitivament.",
          },
          folderForCustomer: {
            label: 'Carpeta per a cada client',
            help: 'Buit vol dir apagat. Escriu la ruta de la carpeta que s’obrirà en donar d’alta un client, per exemple «Clients / [customer]». Paraules disponibles: [customer], [code], [year], [month]. Diverses carpetes alhora, separades per punt i coma: la primera és la del client.',
          },
          folderForProject: {
            label: 'Carpeta per a cada obra',
            help: 'Buit vol dir apagat. Escriu-la dins del client per tenir una carpeta per obra dins de cada client: «[folder:customer] / [project]». Paraules: [project], [customer], [code], [year], [month], i [folder:customer] per a la carpeta que aquell client ja té.',
          },
          folderForWorkOrder: {
            label: 'Carpeta per a cada ordre de treball',
            help: 'Buit vol dir apagat. Paraules: [code], [customer], [year], [month], [folder:customer].',
          },
          folderForProductionOrder: {
            label: 'Carpeta per a cada ordre de fabricació',
            help: 'Buit vol dir apagat. Paraules: [order], [code], [year], [month].',
          },
          folderForSupplier: {
            label: 'Carpeta per a cada proveïdor',
            help: 'Buit vol dir apagat. Paraules: [supplier], [code], [year], [month].',
          },
          folderForProperty: {
            label: 'Carpeta per a cada emplaçament',
            help: 'Buit vol dir apagat. Paraules: [property], [customer], [code], [year], [month], [folder:customer].',
          },
          folderForAsset: {
            label: 'Carpeta per a cada màquina',
            help: 'Buit vol dir apagat. Paraules: [asset], [code], [site], [year], [month].',
          },
          folderForSopPlan: {
            label: 'Carpeta per a cada cicle de planificació',
            help: 'Buit vol dir apagat. Per exemple «Planificació / [from] a [to]». Paraules: [plan], [code], [site], [from], [to], [year], [month]. [from] i [to] són els mesos que cobreix el pla.',
          },
          fileQuotes: {
            label: 'Arxivar cada pressupost enviat',
            help: 'Buit vol dir apagat: el pressupost s’imprimeix i es descarrega, i no se’n guarda cap còpia. Per exemple «[folder:customer] / Pressupostos». Paraules: [customer], [project], [code], [year], [month], [folder:customer], [folder:project].',
          },
          fileInvoices: {
            label: 'Arxivar cada factura emesa',
            help: 'Buit vol dir apagat. Per exemple «[folder:customer] / Factures / [year]». Paraules: [customer], [project], [number], [series], [year], [month], [folder:customer], [folder:project]. La còpia que es guarda és la que es va emetre, i no se substitueix mai.',
          },
          filePurchaseOrders: {
            label: 'Arxivar cada comanda de compra enviada',
            help: 'Buit vol dir apagat. Per exemple «[folder:supplier] / Comandes». Paraules: [supplier], [code], [year], [month], [folder:supplier].',
          },
          fileProductionOrders: {
            label: 'Arxivar cada ordre de fabricació impresa',
            help: 'Buit vol dir apagat. Per exemple «[folder:project] / Fabricació». Paraules: [order], [code], [project], [year], [month], [folder:project].',
          },
          groups: {
            storage: 'Espai',
            folders: 'Carpetes automàtiques',
            papers: 'Papers que genera l’ERP',
          },
        },
      },
    },
    audit: {
      actions: {
        documents: {
          uploaded: 'Fitxer pujat',
          renamed: 'Fitxer reanomenat',
          moved: 'Fitxer mogut',
          deleted: 'Fitxer enviat a la paperera',
          restored: 'Fitxer restaurat',
          purged: 'Fitxer eliminat definitivament',
          downloaded: 'Fitxer baixat',
          linked: 'Fitxer vinculat a una fitxa',
          unlinked: 'Fitxer desvinculat d’una fitxa',
          folder_created: 'Carpeta creada',
          folder_adopted: 'Carpeta sense propietari assignada',
          folder_renamed: 'Carpeta reanomenada',
          folder_moved: 'Carpeta moguda',
          folder_deleted: 'Carpeta enviada a la paperera',
          folder_restored: 'Carpeta restaurada',
          folder_purged: 'Carpeta eliminada definitivament',
          trash_emptied: 'Paperera buidada',
          viewed: 'Fitxer obert al visor',
          permission_granted: 'Accés concedit',
          permission_changed: 'Accés modificat',
          permission_revoked: 'Accés retirat',
          external_link_shared: 'Enllaç extern creat',
          external_link_revoked: 'Enllaç extern anul·lat',
        },
      },
    },
  },

  en: {
    errors: {
      DOCUMENT_TOO_LARGE: 'That document is too large to be read in a conversation.',
    },
    documents: {
      title: 'Documents',
      subtitle: "The company's filing cabinet: folders, files and who touched them",
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Documents",
        subtitle: "Who gets into each folder, what is checked before a file is stored, and how one leaves the company",
      },
      root: 'Documents',
      breadcrumb: 'Where you are',
      breadcrumbCut: 'There are folders above this one you cannot see',
      searchPlaceholder: 'Search documents…',
      searchingIn: 'Results for “{term}”',
      dashboard: {
        storage: 'Storage',
        storageHelp: 'How much of the document quota is in use, wastebasket included.',
        recent: 'Latest documents',
        recentHelp: 'The five most recently uploaded files, with the folder they are in.',
        documents: 'Documents',
        documentsHelp: 'How many files are filed, not counting the wastebasket.',
      },
      types: {
        folder: 'Folder',
        pdf: 'PDF',
        image: 'Image',
        document: 'Document',
        spreadsheet: 'Spreadsheet',
        presentation: 'Presentation',
        archive: 'Archive',
        text: 'Text',
        other: 'Other',
      },
      references: {
        customer: 'Customer',
        project: 'Project',
        work_order: 'Work order',
        asset: 'Machine',
        supplier: 'Supplier',
        vehicle: 'Vehicle',
        maintenance_plan: 'Maintenance plan',
        invoice: 'Invoice',
        material: 'Material',
        property: 'Site',
        work: 'Construction job',
      },
      actions: {
        new: 'New',
        newFolder: 'New folder',
        upload: 'Upload file',
        create: 'Create',
        cancel: 'Cancel',
        open: 'Open',
        download: 'Download',
        rename: 'Rename',
        move: 'Move to…',
        moveHere: 'Move here',
        details: 'Details',
        info: 'Information',
        delete: 'Delete',
        restore: 'Restore',
        purge: 'Delete for good',
        link: 'Attach',
        unlink: 'Detach',
        clearSearch: 'Clear the search',
        share: 'Share',
      },
      confirm: {
        unlink: '“{name}” will stop appearing on this record. The file is NOT deleted: it stays in the drive, in its folder.',
      },
      table: {
        name: 'Name',
        type: 'Type',
        size: 'Size',
        owner: 'Uploaded by',
        created: 'Created',
        updated: 'Modified',
      },
      filters: {
        title: 'Filters',
        clear: 'Clear the filters',
        type: 'Type',
        allTypes: 'All',
        when: 'Date',
        anyTime: 'Any date',
        today: 'Today',
        week: 'Last 7 days',
        month: 'Last 30 days',
        custom: 'Between two dates',
        from: 'From',
        to: 'To',
        sort: 'Sort by',
      },
      sort: {
        nameAsc: 'Name (A-Z)',
        nameDesc: 'Name (Z-A)',
        newest: 'Newest first',
        oldest: 'Oldest first',
        changed: 'Recently changed',
        largest: 'Largest first',
        smallest: 'Smallest first',
      },
      storage: {
        title: 'Storage',
        used: '{used} of {total}',
      },
      empty: {
        folder: 'This folder is empty',
        folderHint: 'Upload files or create a folder to get started.',
        noResults: 'No files found',
        noResultsHint: 'Try another word, or part of the name.',
        filtered: 'No results',
        filteredHint: 'Try removing a filter.',
      },
      folder: {
        newTitle: 'New folder',
        name: 'Folder name',
        placeholder: 'Customers, Projects, Internal paperwork…',
        required: 'Type a name.',
        info: '{name} · in {path} · created on {created}',
      },
      rename: {
        title: 'Rename',
        label: 'New name',
      },
      move: {
        title: 'Move “{name}”',
        currently: 'Currently in {path}',
        filterPlaceholder: 'Filter folders…',
        noMatch: 'No folder matches',
        done: 'Moved',
      },
      delete: {
        fileTitle: 'Delete this file?',
        fileBody: '“{name}” goes to the wastebasket. You can restore it from there.',
        folderTitle: 'Delete this folder?',
        folderBody: '“{name}” goes to the wastebasket with everything inside it. You can restore it from there.',
        done: 'Sent to the wastebasket',
      },
      details: {
        title: 'Details',
        type: 'Type',
        size: 'Size',
        location: 'Location',
        uploadedBy: 'Uploaded by',
        uploadedAt: 'Uploaded on',
        updatedAt: 'Last modified',
        originalName: 'Original name',
        unknownUser: 'Unassigned',
        access: 'Who has access',
        onlyYou: 'Only you. Nobody else sees it.',
        andMore: 'and {count} more',
        linkedTo: 'Attached to',
        notLinked: 'Not attached to any record.',
        linkTo: 'Attach to a record',
        pickType: 'Pick the kind of record',
        linked: 'Attached',
        checks: 'What was found inside',
      },
      // What the platform inspection found inside a file and let through.
      // Mirrors platform/core/backend/uploads/.
      flags: {
        pdf_javascript: 'This PDF carries JavaScript. It cannot leave the viewer or reach the network, but outside the ERP it can do more.',
        pdf_auto_action: 'This PDF does something on its own when opened: it may put a message on the screen.',
        pdf_dynamic_form: 'This PDF is a dynamic (XFA) form. It may look blank outside Acrobat.',
        pdf_embedded_media: 'This PDF carries embedded multimedia.',
        pdf_embedded_file: 'This PDF carries another file inside it. That is normal in a Factur-X e-invoice.',
        archive_contents_unknown: 'It is an archive: nobody has seen what is inside until it is opened.',
        not_scanned: 'The antivirus could not check it when it was uploaded.',
        scanned: 'Checked by the antivirus on upload.',
      },
      viewer: {
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        noPreview: 'This kind of file cannot be shown here. Download it to open it with your own program.',
      },
      upload: {
        queue: 'Uploads',
        dropHere: 'Drop the files here',
        uploading: 'Uploading {count} file | Uploading {count} files',
        uploaded: '{count} file uploaded | {count} files uploaded',
        failed: '{count} file was not uploaded | {count} files were not uploaded',
        finished: '{count} file uploaded | {count} files uploaded',
        finishing: 'Saving…',
        done: 'Done',
        cancel: 'Cancel this upload',
        cancelled: 'Cancelled',
        tooBig: 'It is {size} and the limit per file is {max}.',
      },
      share: {
        title: 'Share “{name}”',
        peopleHeading: 'People with access',
        addLabel: 'Give somebody access',
        addHelp: 'Type a name or an email. Only people from this company appear.',
        searchPlaceholder: 'Name or email…',
        noPeople: 'Nobody by that name',
        grant: 'Give access',
        granted: '{name} now has access',
        revoke: 'Remove access',
        revoked: '{name} no longer has access',
        revokeTitle: 'Remove {name}’s access?',
        revokeMessage: '{name} will stop seeing “{what}” and everything inside it. Access is cut immediately, not in a while.',
        done: 'Done',
        privateNote: 'What you do not share, nobody else sees — working here is not access. It has to be given.',
        levels: {
          viewer: 'Viewer',
          editor: 'Editor',
          manager: 'Manager',
          owner: 'Owner',
        },
        from: {
          owner: 'Owner',
          direct: 'Direct access',
          inherited: 'Inherited from: {folder}',
          inheritedShort: 'from {folder}',
        },
        external: {
          heading: 'Link for somebody outside',
          unavailable: 'Links for people outside are not switched on for this installation. Ask whoever runs the ERP.',
          new: 'Create a link',
          create: 'Create the link',
          copy: 'Copy',
          copied: 'Link copied',
          copyManually: 'It could not be copied automatically. Select the link and copy it by hand.',
          copyNow: 'Copy it now: it cannot be shown again',
          copyNote: 'This link is not stored anywhere. If you lose it, create another and revoke this one.',
          passwordLabel: 'Password (optional)',
          passwordHelp: 'If you set one, send it by another channel: a password in the same email as the link protects nothing.',
          expiresLabel: 'Expires on',
          expiresHelp: 'Empty uses the default number of days from Settings.',
          never: 'Never expires',
          neverHint: 'A link with no expiry is only closed by revoking it by hand.',
          withPassword: 'With password',
          until: 'Until {date}',
          noExpiry: 'No expiry',
          opened: 'Not opened | Opened once | Opened {count} times',
          revoke: 'Revoke',
          revoked: 'Link revoked',
          revokeTitle: 'Revoke this link?',
          revokeMessage: 'Whoever holds “{name}” stops seeing anything at once, even with it open right now. It cannot be switched back on: you would have to create another.',
          status: {
            revoked: 'Revoked',
            expired: 'Expired',
          },
        },
      },
      shared: {
        title: 'Shared with me',
        subtitle: 'What other people have given you, and is not yours',
        backToDrive: 'Go to the document store',
        owner: 'Owner',
        access: 'Access',
        since: 'Since',
        emptyTitle: 'Nobody has shared anything with you yet',
        emptyHint: 'When somebody gives you access to a folder or a document, it turns up here.',
      },
      notifications: {
        shared: {
          label: 'When somebody shares a folder or a document with you',
          title: '“{name}” was shared with you',
          body: '{who} gave you access to “{name}” as {level}.',
        },
      },
      trash: {
        title: 'Wastebasket',
        subtitle: 'What has been deleted, and can still be brought back',
        explain: 'What is in here still takes up space until it is deleted for good. Emptying the wastebasket is what frees storage.',
        deletedBy: 'Deleted by',
        deletedAt: 'Date',
        emptyState: 'The wastebasket is empty',
        emptyStateHint: 'What you delete shows up here, until you delete it for good.',
        empty: 'Empty the wastebasket',
        emptyTitle: 'Empty the wastebasket?',
        emptyBody: 'Everything in it is deleted for good. This cannot be undone.',
        emptied: '{count} files were deleted',
        purgeTitle: 'Delete for good?',
        purgeBody: '“{name}” is deleted for good. This cannot be undone.',
        purged: 'Deleted for good',
        restoredInto: '“{name}” is back in {folder}',
        restoredRoot: '“{name}” is back in Documents',
      },
      pager: {
        previous: 'Previous',
        next: 'Next',
        of: 'Page {page} of {pages}',
      },
    },
    modules: { documents: 'Documents' },
    assistant: {
      openers: {
        documents: {
          find: 'Where is the contract for…?',
          space: 'How much document storage is left?',
          recent: 'What has been uploaded recently?',
        },
      },
    },
    permissions: {
      documents: {
        read: 'See and download documents',
        write: 'Upload, create folders, rename and move',
        delete: 'Send to the wastebasket and restore',
        manage: 'Empty the wastebasket and delete for good',
        admin: 'See and administer the WHOLE document store, everybody’s',
        share_external: 'Share by link with somebody who has no account here',
      },
    },
    settings: {
      ns: {
        documents: {
          title: 'Documents',
          subtitle: 'How much it may hold, and what is filed on its own',
          storageQuotaGb: {
            label: 'Storage for documents (GB)',
            help: 'How much the whole filing cabinet may hold. What is in the wastebasket counts too, because it still occupies the disk: emptying it is what frees space.',
          },
          maxFileMb: {
            label: 'Largest single file (MB)',
            help: 'A scanned contract or a photograph from a phone fits in 25 MB. Raising it lets bigger files in, and makes a slow connection wait longer before finding out one was refused.',
          },
          externalLinkDays: {
            label: 'Days an external link lasts',
            help: 'How long a link shared with somebody who has no account in the ERP lasts when whoever creates it says nothing else. Another date can be set when creating it, and a link with no expiry needs the permission that deletes files for good.',
          },
          folderForCustomer: {
            label: 'Folder to open for each customer',
            help: 'Empty means off. The path of the folder to open when a customer is created, for example «Customers / [customer]». Words you can use: [customer], [code], [year], [month]. Several folders at once, separated by a semicolon — the first one is the customer’s own.',
          },
          folderForProject: {
            label: 'Folder to open for each project',
            help: 'Empty means off. Write it under the customer to get a folder per project inside each customer: «[folder:customer] / [project]». Words: [project], [customer], [code], [year], [month], and [folder:customer] for the folder that customer already has.',
          },
          folderForWorkOrder: {
            label: 'Folder to open for each work order',
            help: 'Empty means off. Words: [code], [customer], [year], [month], [folder:customer].',
          },
          folderForProductionOrder: {
            label: 'Folder to open for each manufacturing order',
            help: 'Empty means off. Words: [order], [code], [year], [month].',
          },
          folderForSupplier: {
            label: 'Folder to open for each supplier',
            help: 'Empty means off. Words: [supplier], [code], [year], [month].',
          },
          folderForProperty: {
            label: 'Folder to open for each site',
            help: 'Empty means off. Words: [property], [customer], [code], [year], [month], [folder:customer].',
          },
          folderForAsset: {
            label: 'Folder to open for each machine',
            help: 'Empty means off. Words: [asset], [code], [site], [year], [month].',
          },
          folderForSopPlan: {
            label: 'Folder to open for each planning cycle',
            help: 'Empty means off. For example «Planning / [from] to [to]». Words: [plan], [code], [site], [from], [to], [year], [month]. [from] and [to] are the months the plan covers.',
          },
          fileQuotes: {
            label: 'File each quote that is sent',
            help: 'Empty means off: the quote is printed and downloaded, and no copy is kept. For example «[folder:customer] / Quotes». Words: [customer], [project], [code], [year], [month], [folder:customer], [folder:project].',
          },
          fileInvoices: {
            label: 'File each invoice that is issued',
            help: 'Empty means off. For example «[folder:customer] / Invoices / [year]». Words: [customer], [project], [number], [series], [year], [month], [folder:customer], [folder:project]. The copy that is kept is the one that was issued, and it is never replaced.',
          },
          filePurchaseOrders: {
            label: 'File each purchase order that is sent',
            help: 'Empty means off. For example «[folder:supplier] / Orders». Words: [supplier], [code], [year], [month], [folder:supplier].',
          },
          fileProductionOrders: {
            label: 'File each manufacturing order that is printed',
            help: 'Empty means off. For example «[folder:project] / Manufacturing». Words: [order], [code], [project], [year], [month], [folder:project].',
          },
          groups: {
            storage: 'Space',
            folders: 'Automatic folders',
            papers: 'Papers the ERP prints',
          },
        },
      },
    },
    audit: {
      actions: {
        documents: {
          uploaded: 'File uploaded',
          renamed: 'File renamed',
          moved: 'File moved',
          deleted: 'File sent to the wastebasket',
          restored: 'File restored',
          purged: 'File deleted for good',
          downloaded: 'File downloaded',
          linked: 'File attached to a record',
          unlinked: 'File detached from a record',
          folder_created: 'Folder created',
          folder_adopted: 'Ownerless folder assigned',
          folder_renamed: 'Folder renamed',
          folder_moved: 'Folder moved',
          folder_deleted: 'Folder sent to the wastebasket',
          folder_restored: 'Folder restored',
          folder_purged: 'Folder deleted for good',
          trash_emptied: 'Wastebasket emptied',
          viewed: 'File opened in the viewer',
          permission_granted: 'Access granted',
          permission_changed: 'Access changed',
          permission_revoked: 'Access removed',
          external_link_shared: 'External link created',
          external_link_revoked: 'External link revoked',
        },
      },
    },
  },

  fr: {
    errors: {
      DOCUMENT_TOO_LARGE: 'Ce document est trop volumineux pour être lu dans une conversation.',
    },
    documents: {
      title: 'Documents',
      subtitle: "L'armoire de l'entreprise : dossiers, fichiers et qui y a touché",
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Documents",
        subtitle: "Qui entre dans chaque dossier, ce qui est contrôlé avant d'enregistrer un fichier et comment l'un d'eux sort de l'entreprise",
      },
      root: 'Documents',
      breadcrumb: 'Où vous êtes',
      breadcrumbCut: 'Il y a au-dessus des dossiers auxquels vous n’avez pas accès',
      searchPlaceholder: 'Rechercher dans les documents…',
      searchingIn: 'Résultats pour « {term} »',
      dashboard: {
        storage: 'Stockage',
        storageHelp: 'Quel pourcentage du quota de documents est occupé, corbeille comprise.',
        recent: 'Derniers documents',
        recentHelp: 'Les cinq fichiers déposés le plus récemment, avec le dossier où ils se trouvent.',
        documents: 'Documents',
        documentsHelp: 'Combien de fichiers sont classés, sans compter la corbeille.',
      },
      types: {
        folder: 'Dossier',
        pdf: 'PDF',
        image: 'Image',
        document: 'Document',
        spreadsheet: 'Tableur',
        presentation: 'Présentation',
        archive: 'Archive',
        text: 'Texte',
        other: 'Autre',
      },
      references: {
        customer: 'Client',
        project: 'Chantier',
        work_order: 'Ordre de travail',
        asset: 'Machine',
        supplier: 'Fournisseur',
        vehicle: 'Véhicule',
        maintenance_plan: 'Plan de maintenance',
        invoice: 'Facture',
        material: 'Matériel',
        property: 'Site',
        work: 'Chantier de construction',
      },
      actions: {
        new: 'Nouveau',
        newFolder: 'Nouveau dossier',
        upload: 'Déposer un fichier',
        create: 'Créer',
        cancel: 'Annuler',
        open: 'Ouvrir',
        download: 'Télécharger',
        rename: 'Renommer',
        move: 'Déplacer vers…',
        moveHere: 'Déplacer ici',
        details: 'Détails',
        info: 'Informations',
        delete: 'Supprimer',
        restore: 'Restaurer',
        purge: 'Supprimer définitivement',
        link: 'Rattacher',
        unlink: 'Détacher',
        clearSearch: 'Effacer la recherche',
        share: 'Partager',
      },
      confirm: {
        unlink: '« {name} » n’apparaîtra plus sur cette fiche. Le fichier n’est PAS supprimé : il reste dans l’archivage, dans son dossier.',
      },
      table: {
        name: 'Nom',
        type: 'Type',
        size: 'Taille',
        owner: 'Déposé par',
        created: 'Créé',
        updated: 'Modifié',
      },
      filters: {
        title: 'Filtres',
        clear: 'Retirer les filtres',
        type: 'Type',
        allTypes: 'Tous',
        when: 'Date',
        anyTime: "N'importe quelle date",
        today: "Aujourd'hui",
        week: '7 derniers jours',
        month: '30 derniers jours',
        custom: 'Entre deux dates',
        from: 'Du',
        to: 'Au',
        sort: 'Trier par',
      },
      sort: {
        nameAsc: 'Nom (A-Z)',
        nameDesc: 'Nom (Z-A)',
        newest: 'Plus récents',
        oldest: 'Plus anciens',
        changed: 'Modifiés récemment',
        largest: 'Plus volumineux',
        smallest: 'Moins volumineux',
      },
      storage: {
        title: 'Stockage',
        used: '{used} sur {total}',
      },
      empty: {
        folder: 'Ce dossier est vide',
        folderHint: 'Déposez des fichiers ou créez un dossier pour commencer.',
        noResults: 'Aucun fichier trouvé',
        noResultsHint: 'Essayez un autre mot, ou une partie du nom.',
        filtered: 'Aucun résultat',
        filteredHint: 'Essayez de retirer un filtre.',
      },
      folder: {
        newTitle: 'Nouveau dossier',
        name: 'Nom du dossier',
        placeholder: 'Clients, Chantiers, Documentation interne…',
        required: 'Saisissez un nom.',
        info: '{name} · dans {path} · créé le {created}',
      },
      rename: {
        title: 'Renommer',
        label: 'Nouveau nom',
      },
      move: {
        title: 'Déplacer « {name} »',
        currently: 'Actuellement dans {path}',
        filterPlaceholder: 'Filtrer les dossiers…',
        noMatch: 'Aucun dossier ne correspond',
        done: 'Déplacé',
      },
      delete: {
        fileTitle: 'Supprimer ce fichier ?',
        fileBody: '« {name} » part à la corbeille. Vous pourrez le restaurer depuis là.',
        folderTitle: 'Supprimer ce dossier ?',
        folderBody: '« {name} » part à la corbeille avec tout ce qu’il contient. Vous pourrez le restaurer depuis là.',
        done: 'Envoyé à la corbeille',
      },
      details: {
        title: 'Détails',
        type: 'Type',
        size: 'Taille',
        location: 'Emplacement',
        uploadedBy: 'Déposé par',
        uploadedAt: 'Date de dépôt',
        updatedAt: 'Dernière modification',
        originalName: 'Nom d’origine',
        unknownUser: 'Non attribué',
        access: 'Qui a accès',
        onlyYou: 'Vous seul. Personne d’autre ne le voit.',
        andMore: 'et {count} de plus',
        linkedTo: 'Rattaché à',
        notLinked: 'Rattaché à aucune fiche.',
        linkTo: 'Rattacher à une fiche',
        pickType: 'Choisissez le type de fiche',
        linked: 'Rattaché',
        checks: 'Ce qui a été trouvé à l’intérieur',
      },
      // Ce que l’inspection de la plateforme a trouvé dans un fichier et a
      // laissé passer. Miroir de platform/core/backend/uploads/.
      flags: {
        pdf_javascript: 'Ce PDF contient du JavaScript. Il ne peut ni sortir de la visionneuse ni accéder au réseau, mais hors de l’ERP il peut faire davantage.',
        pdf_auto_action: 'Ce PDF agit de lui-même à l’ouverture : il peut afficher un message à l’écran.',
        pdf_dynamic_form: 'Ce PDF est un formulaire dynamique (XFA). Il peut apparaître vide hors d’Acrobat.',
        pdf_embedded_media: 'Ce PDF contient du multimédia incorporé.',
        pdf_embedded_file: 'Ce PDF contient un autre fichier. C’est le cas normal d’une facture électronique Factur-X.',
        archive_contents_unknown: 'C’est une archive : personne n’a vu ce qu’elle contient avant de l’ouvrir.',
        not_scanned: 'L’antivirus n’a pas pu la contrôler au moment de l’envoi.',
        scanned: 'Contrôlé par l’antivirus lors de l’envoi.',
      },
      viewer: {
        zoomIn: 'Agrandir',
        zoomOut: 'Réduire',
        noPreview: 'Ce type de fichier ne peut pas être affiché ici. Téléchargez-le pour l’ouvrir avec votre programme.',
      },
      upload: {
        queue: 'Dépôts',
        dropHere: 'Déposez les fichiers ici',
        uploading: 'Dépôt de {count} fichier | Dépôt de {count} fichiers',
        uploaded: '{count} fichier déposé | {count} fichiers déposés',
        failed: "{count} fichier n'a pas été déposé | {count} fichiers n'ont pas été déposés",
        finished: '{count} fichier déposé | {count} fichiers déposés',
        finishing: 'Enregistrement…',
        done: 'Terminé',
        cancel: 'Annuler ce dépôt',
        cancelled: 'Annulé',
        tooBig: 'Il fait {size} et la limite par fichier est {max}.',
      },
      share: {
        title: 'Partager « {name} »',
        peopleHeading: 'Personnes ayant accès',
        addLabel: 'Donner accès à quelqu’un',
        addHelp: 'Saisissez un nom ou un e-mail. Seules les personnes de cette entreprise apparaissent.',
        searchPlaceholder: 'Nom ou e-mail…',
        noPeople: 'Personne de ce nom',
        grant: 'Donner accès',
        granted: '{name} a maintenant accès',
        revoke: 'Retirer l’accès',
        revoked: '{name} n’a plus accès',
        revokeTitle: 'Retirer l’accès à {name} ?',
        revokeMessage: '{name} ne verra plus « {what} » ni ce qu’il contient. L’accès est coupé immédiatement, pas dans un moment.',
        done: 'Terminé',
        privateNote: 'Ce que vous ne partagez pas, personne d’autre ne le voit — travailler ici ne donne pas accès. Il faut le donner.',
        levels: {
          viewer: 'Lecteur',
          editor: 'Éditeur',
          manager: 'Gestionnaire',
          owner: 'Propriétaire',
        },
        from: {
          owner: 'Propriétaire',
          direct: 'Accès direct',
          inherited: 'Accès hérité de : {folder}',
          inheritedShort: 'de {folder}',
        },
        external: {
          heading: "Lien pour quelqu'un de l'extérieur",
          unavailable: "Les liens pour des personnes extérieures ne sont pas activés sur cette installation. Demandez-le à qui administre l'ERP.",
          new: 'Créer un lien',
          create: 'Créer le lien',
          copy: 'Copier',
          copied: 'Lien copié',
          copyManually: 'La copie automatique a échoué. Sélectionnez le lien et copiez-le à la main.',
          copyNow: 'Copiez-le maintenant : il ne pourra plus être affiché',
          copyNote: "Ce lien n'est enregistré nulle part. Si vous le perdez, créez-en un autre et révoquez celui-ci.",
          passwordLabel: 'Mot de passe (facultatif)',
          passwordHelp: "Si vous en mettez un, envoyez-le par un autre canal : un mot de passe dans le même e-mail que le lien ne protège de rien.",
          expiresLabel: 'Expire le',
          expiresHelp: 'Vide utilise le nombre de jours par défaut des Réglages.',
          never: "N'expire jamais",
          neverHint: "Un lien sans expiration ne se ferme qu'en le révoquant à la main.",
          withPassword: 'Avec mot de passe',
          until: "Jusqu'au {date}",
          noExpiry: "Pas d'expiration",
          opened: "Jamais ouvert | Ouvert une fois | Ouvert {count} fois",
          revoke: 'Révoquer',
          revoked: 'Lien révoqué',
          revokeTitle: 'Révoquer ce lien ?',
          revokeMessage: "Celui qui détient « {name} » ne verra plus rien immédiatement, même s'il l'a ouvert en ce moment. On ne peut pas le réactiver : il faudrait en créer un autre.",
          status: {
            revoked: 'Révoqué',
            expired: 'Expiré',
          },
        },
      },
      shared: {
        title: 'Partagé avec moi',
        subtitle: 'Ce que d’autres personnes vous ont donné et qui n’est pas à vous',
        backToDrive: 'Aller à la gestion documentaire',
        owner: 'Propriétaire',
        access: 'Accès',
        since: 'Depuis',
        emptyTitle: 'Personne ne vous a encore rien partagé',
        emptyHint: 'Quand quelqu’un vous donnera accès à un dossier ou à un document, cela apparaîtra ici.',
      },
      notifications: {
        shared: {
          label: 'Quand quelqu’un partage un dossier ou un document avec vous',
          title: '« {name} » a été partagé avec vous',
          body: '{who} vous a donné accès à « {name} » en tant que {level}.',
        },
      },
      trash: {
        title: 'Corbeille',
        subtitle: 'Ce qui a été supprimé, et peut encore être récupéré',
        explain: "Ce qui se trouve ici occupe encore de l'espace jusqu'à sa suppression définitive. Vider la corbeille est ce qui libère du stockage.",
        deletedBy: 'Supprimé par',
        deletedAt: 'Date',
        emptyState: 'La corbeille est vide',
        emptyStateHint: 'Ce que vous supprimez apparaît ici, jusqu’à la suppression définitive.',
        empty: 'Vider la corbeille',
        emptyTitle: 'Vider la corbeille ?',
        emptyBody: 'Tout ce qu’elle contient est supprimé définitivement. Cela ne peut pas être annulé.',
        emptied: '{count} fichiers ont été supprimés',
        purgeTitle: 'Supprimer définitivement ?',
        purgeBody: '« {name} » est supprimé définitivement. Cela ne peut pas être annulé.',
        purged: 'Supprimé définitivement',
        restoredInto: '« {name} » est de retour dans {folder}',
        restoredRoot: '« {name} » est de retour dans Documents',
      },
      pager: {
        previous: 'Précédent',
        next: 'Suivant',
        of: 'Page {page} sur {pages}',
      },
    },
    modules: { documents: 'Documents' },
    assistant: {
      openers: {
        documents: {
          find: 'Où est le contrat de… ?',
          space: 'Combien de stockage reste-t-il dans Documents ?',
          recent: 'Qu’est-ce qui a été déposé récemment ?',
        },
      },
    },
    permissions: {
      documents: {
        read: 'Voir et télécharger les documents',
        write: 'Déposer, créer des dossiers, renommer et déplacer',
        delete: 'Envoyer à la corbeille et restaurer',
        manage: 'Vider la corbeille et supprimer définitivement',
        admin: 'Voir et administrer TOUTE la gestion documentaire, de tout le monde',
        share_external: "Partager par lien avec quelqu'un sans compte ici",
      },
    },
    settings: {
      ns: {
        documents: {
          title: 'Documents',
          subtitle: 'Ce qu’il peut contenir, et ce qui se classe tout seul',
          storageQuotaGb: {
            label: 'Stockage pour les documents (Go)',
            help: "Combien l'armoire entière peut contenir. Ce qui est à la corbeille compte aussi, car cela occupe toujours le disque : la vider est ce qui libère de l'espace.",
          },
          maxFileMb: {
            label: 'Taille maximale par fichier (Mo)',
            help: 'Un contrat scanné ou une photo de téléphone tiennent dans 25 Mo. L’augmenter accepte des fichiers plus gros, et fait qu’une connexion lente met plus de temps à découvrir qu’un fichier a été refusé.',
          },
          externalLinkDays: {
            label: 'Jours de validité d\u2019un lien externe',
            help: "Durée d'un lien partagé avec quelqu'un sans compte dans l'ERP lorsque son créateur ne précise rien. Une autre date peut être fixée à la création, et un lien sans expiration exige la permission qui supprime définitivement.",
          },
          folderForCustomer: {
            label: 'Dossier à ouvrir pour chaque client',
            help: 'Vide signifie désactivé. Le chemin du dossier à ouvrir lorsqu’un client est créé, par exemple « Clients / [customer] ». Mots disponibles : [customer], [code], [year], [month]. Plusieurs dossiers à la fois, séparés par un point-virgule : le premier est celui du client.',
          },
          folderForProject: {
            label: 'Dossier à ouvrir pour chaque chantier',
            help: 'Vide signifie désactivé. Écrivez-le sous le client pour obtenir un dossier par chantier dans chaque client : « [folder:customer] / [project] ». Mots : [project], [customer], [code], [year], [month], et [folder:customer] pour le dossier que ce client possède déjà.',
          },
          folderForWorkOrder: {
            label: 'Dossier à ouvrir pour chaque ordre de travail',
            help: 'Vide signifie désactivé. Mots : [code], [customer], [year], [month], [folder:customer].',
          },
          folderForProductionOrder: {
            label: 'Dossier à ouvrir pour chaque ordre de fabrication',
            help: 'Vide signifie désactivé. Mots : [order], [code], [year], [month].',
          },
          folderForSupplier: {
            label: 'Dossier à ouvrir pour chaque fournisseur',
            help: 'Vide signifie désactivé. Mots : [supplier], [code], [year], [month].',
          },
          folderForProperty: {
            label: 'Dossier à ouvrir pour chaque site',
            help: 'Vide signifie désactivé. Mots : [property], [customer], [code], [year], [month], [folder:customer].',
          },
          folderForAsset: {
            label: 'Dossier à ouvrir pour chaque machine',
            help: 'Vide signifie désactivé. Mots : [asset], [code], [site], [year], [month].',
          },
          folderForSopPlan: {
            label: 'Dossier à ouvrir pour chaque cycle de planification',
            help: 'Vide signifie désactivé. Par exemple « Planification / [from] à [to] ». Mots : [plan], [code], [site], [from], [to], [year], [month]. [from] et [to] sont les mois couverts par le plan.',
          },
          fileQuotes: {
            label: 'Classer chaque devis envoyé',
            help: 'Vide signifie désactivé : le devis est imprimé et téléchargé, et aucune copie n’est conservée. Par exemple « [folder:customer] / Devis ». Mots : [customer], [project], [code], [year], [month], [folder:customer], [folder:project].',
          },
          fileInvoices: {
            label: 'Classer chaque facture émise',
            help: 'Vide signifie désactivé. Par exemple « [folder:customer] / Factures / [year] ». Mots : [customer], [project], [number], [series], [year], [month], [folder:customer], [folder:project]. La copie conservée est celle qui a été émise, et elle n’est jamais remplacée.',
          },
          filePurchaseOrders: {
            label: 'Classer chaque commande d’achat envoyée',
            help: 'Vide signifie désactivé. Par exemple « [folder:supplier] / Commandes ». Mots : [supplier], [code], [year], [month], [folder:supplier].',
          },
          fileProductionOrders: {
            label: 'Classer chaque ordre de fabrication imprimé',
            help: 'Vide signifie désactivé. Par exemple « [folder:project] / Fabrication ». Mots : [order], [code], [project], [year], [month], [folder:project].',
          },
          groups: {
            storage: 'Espace',
            folders: 'Dossiers automatiques',
            papers: 'Documents que l’ERP imprime',
          },
        },
      },
    },
    audit: {
      actions: {
        documents: {
          uploaded: 'Fichier déposé',
          renamed: 'Fichier renommé',
          moved: 'Fichier déplacé',
          deleted: 'Fichier envoyé à la corbeille',
          restored: 'Fichier restauré',
          purged: 'Fichier supprimé définitivement',
          downloaded: 'Fichier téléchargé',
          linked: 'Fichier rattaché à une fiche',
          unlinked: 'Fichier détaché d’une fiche',
          folder_created: 'Dossier créé',
          folder_adopted: 'Dossier sans propriétaire attribué',
          folder_renamed: 'Dossier renommé',
          folder_moved: 'Dossier déplacé',
          folder_deleted: 'Dossier envoyé à la corbeille',
          folder_restored: 'Dossier restauré',
          folder_purged: 'Dossier supprimé définitivement',
          trash_emptied: 'Corbeille vidée',
          viewed: 'Fichier ouvert dans la visionneuse',
          permission_granted: 'Accès accordé',
          permission_changed: 'Accès modifié',
          permission_revoked: 'Accès retiré',
          external_link_shared: 'Lien externe créé',
          external_link_revoked: 'Lien externe révoqué',
        },
      },
    },
  },
}

// Las tarjetas del asistente (documents.assistant.*), en su propio fichero.
for (const [locale, block] of Object.entries(assistantMessages)) {
  messages[locale].documents.assistant = { ...messages[locale].documents.assistant, ...block }
}

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

Object.assign(messages, withToolLabels(messages))

export default { routes, navigation, messages }
